import React, { createContext, useContext, useMemo } from "react";
import { useVideoConfig } from "remotion";

/**
 * Output formats of the merchant video. Same timeline everywhere; only the
 * layout adapts.
 *
 *   story   1080×1920  Reels / TikTok / Shorts / Stories (the master)
 *   feed    1080×1350  Instagram / Facebook feed (4:5), and the landing's
 *                      /brands/<slug> hero frame, which is 4:5 on desktop
 *   square  1080×1080  feeds, WhatsApp, newsletters
 *   wide    1920×1080  YouTube, web embeds, newsletter thumbnail
 */
export type MerchantFormat = "story" | "feed" | "square" | "wide";

export const MERCHANT_SIZES: Record<
  MerchantFormat,
  { width: number; height: number }
> = {
  story: { width: 1080, height: 1920 },
  feed: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
  wide: { width: 1920, height: 1080 },
};

/** Scenes are authored on a stage whose short side is always 1080. */
const UNIT = 1080;

export type Stage = {
  /** Logical stage size (short side = 1080). */
  w: number;
  h: number;
  /** Layout family: scenes branch on this, never on the raw size. */
  shape: "tall" | "feed" | "square" | "wide";
  /**
   * Text-safe box (logical px). Story keeps load-bearing text inside the
   * cross-platform Reels/TikTok band (account row on top, caption + CTA
   * stack at the bottom, action rail on the right); the other formats
   * only keep a comfortable margin.
   */
  safe: { top: number; bottom: number; left: number; right: number };
  /**
   * Story: width of the Reels/TikTok action rail (like, comment, share) on
   * the right, 180 in the cross-platform worst case. Right-aligned elements
   * that carry text keep clear of it; centred text stays in `safe`.
   * Other shapes: same as `safe.right`.
   */
  rail: number;
};

const StageContext = createContext<Stage | null>(null);

export const useStage = (): Stage => {
  const stage = useContext(StageContext);
  if (!stage) throw new Error("useStage() must be used inside <StageFrame>.");
  return stage;
};

const shapeOf = (w: number, h: number): Stage["shape"] => {
  const r = w / h;
  if (r > 1.2) return "wide";
  if (r > 0.95) return "square";
  if (r > 0.7) return "feed";
  return "tall";
};

const SAFE: Record<Stage["shape"], Stage["safe"]> = {
  tall: { top: 220, bottom: 440, left: 80, right: 130 },
  feed: { top: 72, bottom: 72, left: 72, right: 72 },
  square: { top: 64, bottom: 64, left: 72, right: 72 },
  wide: { top: 72, bottom: 72, left: 110, right: 110 },
};

/** Lays out the logical stage and scales it to the real composition size. */
export const StageFrame: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { width, height } = useVideoConfig();
  const k = Math.min(width, height) / UNIT;
  const w = width / k;
  const h = height / k;

  const stage = useMemo<Stage>(() => {
    const shape = shapeOf(w, h);
    const safe = SAFE[shape];
    return { w, h, shape, safe, rail: shape === "tall" ? 180 : safe.right };
  }, [w, h]);

  return (
    <StageContext.Provider value={stage}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: w,
          height: h,
          overflow: "hidden",
          transform: k === 1 ? undefined : `scale(${k})`,
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
    </StageContext.Provider>
  );
};
